import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { TrashCan } from '../../components/Icons';
import Table from '../../components/Table';
import { domain } from '../../d-man';
import { useCreateUnit, useGetUnits } from '../../queries/Units';
import Pager from '../../components/Pager';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Units(props: RouteComponentProps) {
    const PAGE_SIZE = 30;
    const [skip, setSkip] = useState(0);
    const units = useGetUnits(skip, PAGE_SIZE);

    const newUnit = useCreateUnit();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [description, setDescription] = useState('');

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        if (newUnit.error) {
            setErrorMessage(newUnit.error || 'Failed to create new Unit');
        }
    }, [newUnit.error]);

    const post = async () => {
        setIsLoading(true);
        setErrorMessage('');
        if (!description) {
            setErrorMessage('Please capture all the fields above');
        } else {
            const data = {
                description,
            };
            await newUnit.execute(data);
            units.reset();
        }
        setIsLoading(false);
    };

    const clearFields = () => {
        setErrorMessage('');
        setDescription('');
    };

    /**
     * Does an HTTP Delete to the server for the specified id
     * @param id
     */
    const deleteUnit = async (id?: string) => {
        const remove = domain.delete(`/units/${id}`);
        await remove.execute();
        units.reset();
    };

    const handleNextPage = () => {
        setSkip(skip + PAGE_SIZE);
        units.reset();
    };

    const handlePreviousPage = () => {
        setSkip(Math.max(skip - PAGE_SIZE, 0));
        units.reset();
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-wrap items-end justify-between gap-4 bg-surface border border-border rounded-xl m-4 p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="text-sm font-semibold text-foreground pb-2 pr-4 border-r border-border">
                        New Unit
                    </div>
                    <div className="w-48">
                        <Input
                            label="Description"
                            value={description}
                            type="text"
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {errorMessage && <div className="text-sm text-danger">{errorMessage}</div>}
                    <Button secondary onClick={clearFields}>
                        Clear
                    </Button>
                    <Button onClick={post}>{!isLoading ? 'Submit' : <div>Loading</div>}</Button>
                </div>
            </div>
            <div className="m-4">
                <Table<{ id: string; description: string }>
                    loading={units.loading && 'Fetching Units from server'}
                    error={
                        units.error && `Failed to fetch Units list from the server (${units.error})`
                    }
                    data={units.data?.items}
                    columns={[
                        {
                            key: 'description',
                            title: 'Description',
                        },
                        {
                            key: 'actions',
                            title: '',
                            render: (unit) => (
                                <TrashCan
                                    className="cursor-pointer flex"
                                    id={unit.id}
                                    onClick={() => deleteUnit(unit.id)}
                                />
                            ),
                        },
                    ]}
                />
                {units.data && units.data.totalCount > PAGE_SIZE && (
                    <Pager
                        onNextPage={handleNextPage}
                        onPreviousPage={handlePreviousPage}
                        skip={skip}
                        pageSize={PAGE_SIZE}
                        objectList={units.data}
                    />
                )}
            </div>
        </div>
    );
}
